import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { AlertCircleIcon, Eye, Loader2Icon, ThumbsUpIcon } from "lucide-react";
import { motion } from "motion/react";
import { Suspense } from "react";
import { Link } from "react-router-dom";
import { useComplete } from "../hooks/use-complete";
import { useWidgetParams } from "../hooks/use-widget-params";
import { loadCart } from "../lib/shop-now-cart";
import { CompleteSummary } from "./complete-summary";

export function ReturnRequestComplete() {
  const { status, returnRequestId, error, confirm } = useComplete();
  const { orderId } = useWidgetParams();
  const cart = loadCart(orderId);

  if (status === "pending") {
    return (
      <WidgetPage>
        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mx-auto">
          <Loader2Icon className="size-10 animate-spin text-muted-foreground" />
        </motion.span>
        <Header>
          <HeaderTitle>Envoi en cours…</HeaderTitle>
          <HeaderDescription>
            Veuillez patienter pendant que nous créons votre demande de retour.
          </HeaderDescription>
        </Header>
      </WidgetPage>
    );
  }

  if (status === "success") {
    return (
      <WidgetPage>
        <motion.span
          initial={{ opacity: 0, scale: 0.9, y: 5, rotate: 30 }}
          animate={{ opacity: 1, scale: 1, rotate: 0, y: 0 }}
          exit={{
            opacity: 0,
            scale: 0.98,
            rotate: -10,
            transition: { duration: 0.25, ease: "easeIn" },
          }}
          transition={{ delay: 0.2, type: "spring" }}
          style={{ transformOrigin: "top left" }}
        >
          <ThumbsUpIcon className="text-success-foreground size-10 mx-auto" />
        </motion.span>
        <Header>
          <HeaderTitle>Votre retour a été créé</HeaderTitle>
          <HeaderDescription>
            Nous avons bien enregistré votre demande de retour. Vous recevrez un email de
            confirmation avec les prochaines étapes.
          </HeaderDescription>
        </Header>
        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
            <Link to={`../return-requests/${returnRequestId}`}>
              <Button>
                <Eye />
                Consulter
              </Button>
            </Link>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      </WidgetPage>
    );
  }

  if (status === "error") {
    return (
      <WidgetPage>
        <motion.span
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mx-auto"
        >
          <AlertCircleIcon className="text-destructive size-10 mx-auto" />
        </motion.span>
        <Header>
          <HeaderTitle>Une erreur est survenue</HeaderTitle>
          <HeaderDescription>
            {error?.message ?? "Impossible de créer votre demande de retour."}
          </HeaderDescription>
        </Header>
        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
            <Button onClick={confirm}>Réessayer</Button>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      </WidgetPage>
    );
  }

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>Confirmer votre retour</HeaderTitle>
        <HeaderDescription>Vérifiez le récapitulatif avant de soumettre.</HeaderDescription>
      </Header>
      <Suspense fallback={null}>
        <CompleteSummary orderId={orderId} cart={cart} />
      </Suspense>
      <AnimatedButtonRow variants={{}}>
        <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
          <Button onClick={confirm}>Confirmer</Button>
        </AnimatedButtonContainer>
      </AnimatedButtonRow>
    </WidgetPage>
  );
}
